import { formatIsoDate } from 'utils/date-utils';
import firstCharUpperCase from 'utils/firstCharUpperCase';

export const en = {
  errorMessages: {
    EntityNotFoundException: {
      email:
        'We couldn’t find an account with that email. Double-check the email or sign up to create a new account',
      userId: 'No user found. Please check your credentials and try again',
      companyId:
        "The company you're looking for doesn’t exist or may have been removed",
      eventId:
        'The event you’re looking for doesn’t exist or may have been removed. Double-check and try again',
      planId:
        'The plan you’re looking for doesn’t exist or may have been removed',
      default: 'The requested resource could not be found. Please try again',
    },
    EmailAlreadyExistsException: {
      default:
        'An account with this email already exists. Please try logging in or use a different email to sign up',
    },
    InvalidCurrentPasswordException: {
      default:
        'The current password you entered is incorrect. Double-check and try again',
    },
    AuthorizationException: {
      userId: '',
      companyId: '',
      code: 'Oops, the code you entered is invalid or has expired. Let’s get you back on track—request a new code and try again',
      eventId:
        'You don’t have access to this resource. It seems to belong to another account or company',
      default:
        'Your session has expired or the token is invalid. Please log in again to continue',
    },
    AuthenticationException: {
      default:
        'Unable to sign you in—your credentials don’t match our records. Double-check and try again',
    },
    MethodArgumentNotValidException: {
      default:
        'Some of the information you provided doesn’t meet the required format. Please correct the following fields:',
      email: 'Email: Must be a valid professional email',
      countryCode: 'Country: Cannot be blank and must be 2 characters',
      name: 'Name: Should be between 2 and 100 characters',
      password:
        'Password: Must be at least 8 characters long, include at least 1 digit, and 1 special character (e.g., @, #, $)',
      signingKey: 'Signing key: Cannot be blank',
      reason: 'Reason: Cannot be blank',
      code: 'Code: Cannot be blank',
      refreshToken:
        'Your session has expired or the token is invalid. Please log in again to continue',
    },
    NoLiveSubscriptionException: {
      default:
        'Hmm, there seems to be something disconnected from our servers. Check back in a few moments and it should fix itself',
    },
    NoAvailableProviderSlotException: {
      planType:
        'You have more active providers than the [planType] plan allows. Please deactivate providers or upgrade your plan',
      activeProviders:
        'You have more active providers than allowed. Please deactivate some to proceed',
    },
    ProviderAlreadyActiveException: {
      default: 'This provider is already active. No further action is needed!',
    },
    ProviderAlreadyInactiveException: {
      default:
        'This provider is already inactive. No further action is needed!',
    },
    PaddleException: {
      default:
        'There’s a problem with your payment method. Please update it in Billing Settings, then try changing your plan again.',
    },
    InvalidArgumentsException: {
      status: "You can only mark the event as 'Read' or 'Resolved'",
    },
    SlackException: {
      default:
        'Hmm, there seems to be something disconnected from our servers. Check back in a few moments and it should fix itself',
    },
    AuthenticationException_Generic: {
      default:
        'Your session has expired or the token is invalid. Please log in again to continue',
    },
    AuthorizationException_Generic: {
      default:
        'You don’t have access to this resource. It seems to belong to another account or company',
    },
    NoAvailableProviderSlotException_Generic: {
      planType:
        'You have more active providers than your plan allows. Please deactivate providers or choose a plan that fits your needs',
    },
    NoLiveSubscriptionException_Generic: {
      default:
        "Looks like there's no active subscription. Please check your subscription status or choose a plan",
    },
    EntityNotFoundException_Generic: {
      default:
        "The resource you're looking for doesn’t exist or may have been removed. Please check and try again",
    },
    GenericError: {
      default:
        'Oops, something went wrong on our end. Please try again later. If the issue persists, contact support',
    },
  },

  authContainer: {
    title: 'Welcome back',
    subtitle: 'happy to see you again!',
  },
  loginForm: {
    title: 'Sign in',
    rememberMe: 'Remember me',
    forgetPassword: 'Forgot password?',
    newMemberTxt: ' New to MailTock? ',
    signUpTxt: 'Sign up',
    loginBtnTxt: 'Login',
    passwordField: {
      name: 'password',
      label: 'Password',
      placeholder: 'Password',
    },
    emailField: {
      name: 'email',
      label: 'Email',
      placeholder: 'Email',
    },
  },
  emailProvider: {
    title: 'Email providers',
    startFreeTrial: 'Start free trial',
    subscribeNow: 'Subscribe now',
    upgradeYourPlan: 'Upgrade your plan',
    webhookSetup: 'webhook setup',
    activeEmailProviders: {
      title: 'Active email providers',
      text: 'You currently have no active email providers. Subscribe to MailTock to start tracking your emails seamlessly across various providers',
      textActive:
        'Your active email providers are listed below. You can add more providers or adjust your settings as needed to ensure smooth email tracking across all platforms',
      textActiveEmpty:
        'It looks like you haven’t set up webhooks for any of your email providers yet. Choose a provider from the list below to configure your first webhook and start tracking your emails with MailTock',
    },
    setupGuide: {
      title: val => `Setup guide (${val} steps)`,
      subTitle:
        'Follow this step-by-step guide to add your MailTock webhook to',
      activate: 'Activate',
      deactivate: 'Deactivate',
    },
    setupYourWebhook: {
      title: 'Setup your webhook',
      text: (
        name: string
      ) => `Your unique webhook URL for capturing failed emails from 
            ${firstCharUpperCase(name)} will be displayed here once your subscription
            is active. When available, copy it and add it to your email provider
            to start tracking failed emails right away`,
      textActiveNotReached: (name: string) => `
        Here’s your unique webhook URL for capturing failed emails from ${firstCharUpperCase(name)}. Copy it and add it to your email provider to start tracking failed emails right away
      `,
      textActiveReached: (name: string) => `
        You’ve reached the limit for active email providers on your current plan. Upgrade to access more providers and view their unique webhook URLs. Once upgraded, copy the URL and add it to your ${firstCharUpperCase(name)} account to start tracking failed emails
      `,
      resetWebhook: 'Reset webhook',
      pasteText: provider =>
        `To ensure secure communication, please paste the HTTP webhook signing key you copied from ${provider} into the field below. This key is essential for verifying the authenticity of the data sent to MailTock`,
      pasteTextNoKey: provider =>
        `MailTock automatically authenticates and verifies webhooks from ${provider} using servers IP addresses, so there’s no need to add a signing key`,
      activate: 'Activate',
      deactivate: 'Deactivate',
      urlPlaceholder:
        'Your webhook URL will be displayed here once your subscription is active',
      signInKeyPlaaceholder: 'Signing key',
    },
    deactivateModal: {
      confirm: 'Confirm',
      cancel: 'Cancel',
      title: providerName =>
        `Are you sure you want to deactivate ${providerName}?`,
      subtitle: providerName =>
        `By deactivating ${providerName}, you’ll stop receiving failure email events from ${providerName} through MailTock. However, any previous records will still be accessible in MailTock events`,
    },
    startFreeTrialComp: {
      title: "Welcome! We're so glad you are here",
      subtitle:
        'Let’s help you choose the perfect subscription to get the most out of MailTock and start tracking your emails with ease',
      exploreFirst: 'Explore first',
    },
    downGradePopUp: {
      title: 'Action required',
      subtitle: (palnName, planQuote) =>
        `To proceed with downgrading to the ${palnName} plan, please reduce your active email providers to a maximum of ${planQuote} providers`,
      emailProviders: 'Email providers',
    },
    pendingPaymentWarningPopUp: {
      title: 'You have pending payment',
      subtitle: (planName, total, billedAt) =>
        `To proceed with new subscription you have to pay your pending payment first of $${total} since ${formatIsoDate(billedAt)} for ${planName} plan`,
      payNow: 'Pay now',
    },
    supportedEmailProviders: {
      title: 'Supported email providers',
      subtitle:
        'Explore the list of all supported email providers below. Click on any provider to view detailed setup instructions and integrate it with MailTock effortlessly',
    },
  },
  breadcrumbs: {
    emailProviders: 'Email providers',
    providerDetails: 'Provider details',
    pricing: 'Pricing',
    payment: 'Payment',
    account: 'Account settings',
    changePlan: 'Change plan',
  },
  providers: {
    mailgun: {
      steps: {
        one: {
          partOne: 'Copy the ',
          webhook: 'MailTock webhook URL',
          partThree: ' to your clipboard',
        },
        two: 'Log in to Mailgun and navigate to Send > Sending > Webhooks from the side menu',
        three: 'Click “Add Webhook” in the upper right corner',
        four: {
          partOne:
            'Select “Temporary Failure” from the “Event Type” drop-down list and paste the ',
          webhook: 'MailTock webhook URL',
          partThree: ' into the “URL” field then click “Create Webook”',
        },
        five: {
          partOne:
            'Repeat the process: Click “Add Webhook” again, but this time select “Permanent Failure” from the “Event Type” drop-down list, paste the ',
          webhook: 'MailTock webhook URL',
          partThree: ' into the “URL” field, and click “Create Webhook”',
        },
        six: {
          partOne:
            'Repeat the process: Click “Add Webhook” again, but this time select “Spam Complaints” from the “Event Type” drop-down list, paste the ',
          webhook: 'MailTock webhook URL',
          partThree: ' into the “URL” field, and click “Create Webhook”',
        },
        seven:
          'Scroll down and copy your HTTP webhook signing key from Mailgun',
        eight:
          'Paste the signing key here then click “Activate Mailgun” to complete the setup',
        nine: {
          partOne:
            'Once your Mailgun webhook is activated, MailTock will notify you of any failed emails from Mailgun, and you can monitor them directly in ',
          eventsLink: 'MailTock events',
        },
      },
    },
    sendgrid: {
      steps: {
        one: {
          partOne: 'Copy the ',
          webhook: 'MailTock webhook URL',
          partThree: ' to your clipboard',
        },
        two: 'Log in to SendGrid and navigate to Settings > Mail Settings from the side menu',
        three: 'Under “Webhook Settings”, click “Event Webhooks”',
        four: 'Click “Create new webhook” to open the configuration dialog',
        five: {
          text: 'Configure your webhook as follows:',
          subSteps: [
            'Enable endpoint: Toggle to activate the webhook',
            'Friendly Name: Add an optional name, like "MailTock"',
            {
              partOne: 'Post URL: Paste the ',
              webhook: 'MailTock webhook URL',
            },
            'Actions to be Posted: Select Spam Reports under Engagment data and Dropped, Deferred, and Bounced event types under Deliverability Data',
            'Security features: Toggle “Enable Signed Event Webhook” and click “Save” to generate the verification key',
          ],
        },
        six: ' Confirm the signature verification is enabled by revisiting the dialog and ensuring the “Signature Verification” setting is active with a displayed verification key',
        seven:
          'Copy the “Verification Key” and paste it here, then click “Activate Sendgrid” to complete the setup',
        eight: {
          partOne:
            'Once your Sendgrid webhook is activated, MailTock will notify you of any failed emails from Sendgrid, and you can monitor them directly in ',
          eventsLink: 'MailTock events',
        },
      },
    },
    mailchimp: {
      steps: {
        one: {
          partOne: 'Copy the ',
          webhook: 'MailTock webhook URL',
          partThree: ' to your clipboard',
        },
        two: 'Log in to your Mandrill app (Mailchimp’s transactional email service) and navigate to "Settings" > "Webhooks" from the side menu',
        three: 'Click "Add a Webhook" to open the configuration dialog',
        four: {
          text: 'Configure your webhook as follows:',
          subSteps: [
            'Trigger on Events: Select "Message is soft-bounced", "Message is bounced", "Message is marked as spam", and "Message is rejected"',
            {
              partOne: 'Post To URL: Paste the ',
              webhook: 'MailTock webhook URL',
            },
            'Description: Add a friendly name, like "MailTock"',
          ],
        },
        five: 'Click "Create Webhook" to save your configuration and generate the signing key',
        six: 'Copy your signing key from the Mandrill app',
        seven:
          'Paste it here, then click "Activate Mailchimp" to complete the setup',
        eight: {
          partOne:
            'Once your Mailchimp webhook is activated, MailTock will notify you of any failed emails from Mailchimp, and you can monitor them directly in ',
          eventsLink: 'MailTock events',
        },
      },
    },
    brevo: {
      steps: {
        one: {
          partOne: 'Copy the ',
          webhook: 'MailTock webhook URL',
          partThree: ' to your clipboard',
        },
        two: 'Log in to Brevo and navigate to "Transactional" > "Email" > "Settings" from the side menu',
        three: 'Under “Settings”, click “Webhook”',
        four: 'Click "Add a new webhook" to open the configuration dialog',
        five: {
          text: 'Configure your webhook as follows:',
          subSteps: [
            {
              partOne: 'URL to call: Paste the ',
              webhook: 'MailTock webhook URL',
            },
            'When the message is: Select "Soft Bounce", "Hard Bounce", "Invalid email", "Error", "Deferred", "Complaint", and "Blocked"',
            'Description: Add a friendly name, like "MailTock"',
          ],
        },
        six: 'Click "Save" to save your configuration',
        seven: `Click "Activate Brevo" to complete the setup`,
        eight: {
          partOne:
            'Once your Brevo webhook is activated, MailTock will notify you of any failed emails from Brevo, and you can monitor them directly in ',
          eventsLink: 'MailTock events',
        },
      },
    },
    postMark: {
      steps: {
        one: {
          partOne: 'Copy the ',
          webhook: 'MailTock webhook URL',
          partThree: ' to your clipboard',
        },
        two: 'Log in to Postmark and navigate to "Servers" from the top menu',
        three:
          'Under "Servers", choose the server you want to integrate with MailTock',
        four: 'Under "Message Streams", choose the stream you want to integrate with MailTock',
        five: 'Navigate to "Webhooks" and click "Add webhook" to open the configuration dialog',
        six: {
          text: 'Configure your webhook as follows:',
          subSteps: [
            {
              partOne: 'Webhook URL: Paste the ',
              webhook: 'MailTock webhook URL',
            },
            'Which events should we send?: Select "Bounce", and "Spam Complaint"',
          ],
        },
        seven: 'Click "Save webhook" to save your configuration',
        eight: 'Click "Activate Postmark" to complete the setup ',
        nine: {
          partOne:
            'Once your Postmark webhook is activated, MailTock will notify you of any failed emails from Postmark, and you can monitor them directly in ',
          eventsLink: 'MailTock events',
        },
      },
    },
  },
  errorPages: {
    error: {
      title: 'Sorry it’s not you! It’s us',
      subTitle:
        'Hmm, there seems to be something disconnected from our servers. Check back in a few moments and it should fix itself',
    },
    notFound: {
      title: 'We lost this page',
      subTitle:
        'We searched high and low but couldn’t find what you are looking for. Let’s find a better place for you to go',
    },
  },
  pricing: {
    title: 'Pricing',
    pricingTitle: 'Pricing plans built for businesses of all sizes',
    pricingSubtitle: 'Three simple plans, each with a',
    pricingSubtitleBlack: ' 14-day free trial',
    pricingSubtitleChange: 'Choose a new plan that will be applied instantly',
    pricingSubtitleResume:
      'Resume the current plan you already cancelled, or choose a new one that will fit your needs',
    changePlan: 'Change Plan',
    monthly: 'Monthly',
    yearly: 'Yearly',
    save: 'Save 20%',
    notInterested: 'Not interested in a new plan? You can',
    cancel: 'cancel',
    yourSubscription: 'your subscription',
    plans: [
      {
        subtitle: 'Ideal for smaller businesses or startups',
      },
      {
        subtitle: 'Designed for growing businesses',
      },
      {
        subtitle: 'Perfect for large businesses or enterprises',
      },
    ],
    one: num => `${num} failed email event${num == 1 ? '' : 's'} per month`,
    two: num => `${num} email provider${num == 1 ? '' : 's'}`,
    perMonth: '/month',
    billedYearly: 'Billed yearly',
    billedMonthly: 'Billed monthly',
    plansHelp: {
      title: '💡 Need help choosing the right plan?',
      text: 'On average, businesses see a bounce rate of around 0.5% to 5% for their emails. Use this benchmark to select the plan that best suits your needs',
    },
    plansNote: {
      title: 'Worried about exceeding your plan’s quota?',
      text: (fee: number, emails: number) =>
        `We’ve got you covered. If you exceed your plan’s monthly quota, a flexible <b>overage fee</b> of <b>$${fee?.toLocaleString()} per ${emails?.toLocaleString()}</b> additional emails will apply. This amount will be fully charged when you open a new block of ${emails?.toLocaleString()} emails. Rest assured, we’re here to ensure "<b>You never lose money to a missed email again!</b>"`,
    },
  },
  plans: {
    activePlanTitle: 'Active plan',
    noPlanTitle: 'You currently have no subscription plan',

    starter: {
      subtitle: 'Ideal for smaller businesses or startups',
      one: num => `${num} failed email event${num == 1 ? '' : 's'} per month`,
      two: num => `${num} email provider${num == 1 ? '' : 's'}`,
    },
    growth: {
      subtitle: 'Designed for growing businesses',
      one: num => `${num} failed email event${num == 1 ? '' : 's'} per month`,
      two: num => `${num} email provider${num == 1 ? '' : 's'}`,
    },
    ultimate: {
      subtitle: 'Perfect for large businesses or enterprises',
      one: num => `${num} failed email event${num == 1 ? '' : 's'} per month`,
      two: num => `${num} email provider${num == 1 ? '' : 's'}`,
    },
  },
  payment: {
    title: 'Payment',
    dataTitle: 'Ready to experience MailTock’s powerful features?',
    dataSubtitle: `You’re just a step away from experiencing MailTock's full range of features. To get started with your <span>14-day free trial</span>, please enter your card details below`,
    planInfo: {
      title: 'What to expect?',
      notes: [
        {
          title: 'Enjoy a risk-free trial',
          text: 'Your card will not be charged during the 14-day free trial period',
        },
        {
          title: 'Seamless transition',
          text: 'If you choose to continue with MailTock after your trial, we’ll start billing you according to the plan you select',
        },
        {
          title: 'No obligation',
          text: 'Cancel anytime during your trial with no charges applied, ensuring you only pay if you’re satisfied',
        },
      ],
    },
    details: {
      subtotal: 'Subtotal',
      tax: 'Tax',
      due: 'Due',
    },
    success: {
      freeTrialtitle: 'Congratulations! 🎉<br> Your free trial is now active!',
      planTitle: 'Congratulations! 🎉<br> Your plan is now active!',
      subtitle: (planName: string) =>
        `You’ve successfully activated your ${decodeURI(planName)} plan. You're all set to track and optimize your email communication with MailTock!`,
      setup: 'Set up your first email provider',
    },
    fail: {
      title: 'Uh-oh! Payment Failed!',
      subtitle: `It looks like something went wrong while saving your card. Please give it another try!`,
      tryAgain: 'Try again',
    },
  },
  account: {
    title: 'Account settings',
    buttons: [
      {
        title: 'Account details',
        subtitle: 'Manage personal and company information',
      },
      { title: 'Billing', subtitle: 'Saved cards, active plan, and invoices' },
      { title: 'Security', subtitle: 'Update your password' },
      { title: 'Support', subtitle: 'Contact us for inquiries and support' },
      {
        title: 'About MailTock',
        subtitle:
          'Review our terms and conditions, privacy policy, and refund policy',
      },
      { title: 'Logout', subtitle: '' },
      { title: 'Notifications', subtitle: 'Manage notification channels' },
      { title: 'Common issues', subtitle: 'View common issues and solutions' },
    ],
    billing: {
      title: 'Billing',
      savedCards: 'Saved cards',
      noCards: 'You haven’t saved any cards yet',
      cancelSubscription: {
        name: 'Cancel subscription',
        title: 'Thinking of canceling? Let’s stay connected!',
        subtitle: `We'd love to know if there's anything we can do to make your experience better. If you choose to cancel, you’ll remain subscribed until the end of your current billing cycle, and remember, you can always reactivate your subscription whenever you're ready!`,
        cancelationReason: 'Cancellation Reason',
        leaveAComment: 'Leave a comment.. (optional)',
        keepMyPlan: 'Keep my plan',
        proceedToCancel: 'Proceed to cancel',
      },
    },
    details: {
      title: 'Account details',
      prsonalInfo: 'Personal info',
      companyInfo: 'Company info',
      fullname: 'Full name',
      Country: 'Country',
      email: 'Email',
      saveUpdates: 'Save updates',
      companyName: 'Company name',
      companySize: 'Company size',
      industry: 'Industry',
      role: 'Role/Job title (optional)',
      updatePersonalSuccess: 'Personal info has been updated successfully',
      updateCompanySuccess: 'Company info has been updated successfully',
      updatePasswordSuccess: 'Your password has been changed successfully',
    },
    security: {
      title: 'Security',
      changePassword: 'Change password',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmNewPassword: 'Confirm new password',
      confirmPassword: 'Confirm password',
    },
    support: {
      title: 'Support',
      haveQuestion: 'Have questions or need assistance?',
      useTheForm:
        'Fill out the form below, and our team will get back to you shortly via email',
      fullname: 'Full name',
      inquiryType: 'Inquiry topic',
      email: 'Email',
      subjectLine: 'Subject line',
      yourMessage: 'Your message',
      submit: 'Submit',
      success:
        'Thank you for reaching out! 🎉 Your support ticket has been submitted successfully. Our team will review it and get back to you shortly via email',
    },
    about: {
      title: 'About MailTock',
      learnMore: 'Learn more about MailTock',
      explore: `Explore our policies to understand how we operate and protect your information. Your trust is important to us, and we're committed to
          transparency`,
      links: {
        termsAndConditions: 'Terms and conditions',
        privacyPolicy: 'Privacy policy',
        refundPolicy: 'Refund policy',
      },
      termsAndConditions: {
        title: 'Terms and conditions',
      },
      privacyPolicy: {
        title: 'Privacy policy',
      },
      refundPolicy: {
        title: 'Refund policy',
      },
    },
  },
  events: {
    title: 'Events',
    stats: {
      title: 'Statistics',
    },
  },
};
